import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

const translations = {
  sv: {
    // Navbar
    nav: {
      about: "Vad är Vardagslyx?",
      experiences: "Upplevelser",
      inspiration: "Inspiration",
      faq: "Vanliga Frågor",
      contact: "Kontakt",
      customerService: "Kundservice",
      membership: "Bli Medlem",
      partner: "Bli Partner",
      login: "Logga in",
      logout: "Logga ut",
      cart: "Varukorg",
      lang: "EN",
    },
    // Home page
    home: {
      heroTitle: "Ett abonnemang –",
      heroSubtitle: "för en enklare och mer lyxig vardag",
      heroDesc: "Samla poäng varje månad och använd dem till det du gillar.",
      heroTagline: "Vardagslyx gör det enkelt att prioritera det som ger energi i vardagen.",
      exploreBtn: "Utforska upplevelser",
      memberBtn: "Bli Medlem",
      categoriesTitle: "Utforska kategorier",
      categoriesSubtitle: "Upptäck upplevelser inom olika områden",
      valueTitle: "Allt du värdesätter, samlat i ett medlemskap",
      valueDesc: "Upptäck upplevelser, tjänster och möjligheter som förgyller din vardag.",
      valueTagline: "Med Vardagslyx får du allt samlat på ett ställe – så att du slipper leta och istället kan fokusera på att leva.",
      howTitle: "Gör det enkelt att unna dig mer",
      howSubtitle: "Samla poäng varje månad och använd dem till det du gillar.",
      step1Title: "Bli medlem",
      step1Desc: "Skapa ett konto och välj ditt medlemskap",
      step2Title: "Utforska & boka",
      step2Desc: "Hitta upplevelser som passar dig",
      step3Title: "Samla poäng",
      step3Desc: "Tjäna poäng vid varje bokning",
      forAllTitle: "Vardagslyx ska vara för alla",
      forAllDesc: "Lyx handlar inte om pris – det handlar om känslan av att prioritera sig själv och sin vardag.",
      forAllTagline: "Oavsett plånbok ska Vardagslyx göra det möjligt att skapa mer livskvalitet i livet.",
      forAllQuote: "\"Hur mycket mer lyx vill du ge dig själv?\"",
      simpleTitle: "Enkelt. Smidigt. Tillgängligt.",
      simpleDesc: "Med Vardagslyx får du tillgång till ett brett utbud av upplevelser och tjänster – från spa och massage till matupplevelser och aktiviteter. Allt samlat på ett ställe.",
      simplePoint1: "Poäng som kan användas fritt",
      simplePoint2: "Nya upplevelser varje månad",
      simplePoint3: "Flexibelt medlemskap",
      maxTitle: "Få mer värde i ett och samma medlemskap",
      maxDesc: "Kombinera tjänster, upplevelser och erbjudanden för att maximera värdet av ditt medlemskap.",
      maxTagline: "Ju mer du använder Vardagslyx, desto mer får du tillbaka.",
      maxExploreBtn: "Börja utforska",
      maxMemberBtn: "Bli Medlem",
      memberPromoTitle: "Bli medlem idag",
      memberPromoDesc: "Börja samla poäng och upptäck en värld av upplevelser. Ditt första steg mot mer vardagslyx.",
      memberPromoBtn: "Bli medlem nu",
      experiences: "upplevelser",
      popularTitle: "Populära upplevelser",
      popularSubtitle: "Upptäck våra mest populära upplevelser och behandlingar",
      showAll: "Visa alla",
      trustRating: "4.8 i betyg",
      trustRatingDesc: "Baserat på tusentals recensioner",
      trustCustomers: "50,000+",
      trustCustomersDesc: "Nöjda kunder",
      trustExperiences: "200+",
      trustExperiencesDesc: "Unika upplevelser",
    },
    // Quotes
    quotes: [
      "En enklare vardag. En rikare känsla.",
      "Mer upplevelser i livet. Mindre planering.",
      "Lyx i vardagen – på ditt sätt.",
      "Ett medlemskap för mer livskvalitet.",
    ],
    // Categories
    categories: {
      restaurang: "Restaurang & Mat",
      wellness: "Wellness & Spa",
      traning: "Träning & Hälsa",
      kultur: "Kultur & Aktiviteter",
      hotell: "Hotell & Resor",
      hem: "Hem & Vardagstjänster",
    },
    // About page
    about: {
      heroTitle: "Vad är Vardagslyx?",
      heroSubtitle: "En plattform som samlar upplevelser och tjänster för en enklare och mer lyxig vardag",
      intro: "Vardagslyx är ett medlemskap som ger dig tillgång till upplevelser och tjänster som gör livet lite bättre. Vi tror att lyx inte handlar om pris – det handlar om känslan av att prioritera sig själv och sin vardag.",
      visionTitle: "Vår vision",
      visionText: "I en värld full av stress och krav vill vi göra det enkelt att unna sig mer. Vardagslyx samlar allt du behöver för att skapa mer balans, glädje och upplevelser i vardagen – på ett och samma ställe.",
      visionMonth1: "En månad kanske du vill:",
      visionList1: ["boka spa", "träna", "beställa hem mat"],
      visionMonth2: "En annan månad kanske du vill:",
      visionList2: ["boka hotell", "testa en aktivitet", "upptäcka något nytt i din stad"],
      visionConclusion: "Med Vardagslyx använder du ditt medlemskap på det som passar dig just nu.",
      howTitle: "Hur det fungerar",
      howDesc: "Som medlem får du tillgång till ett brett utbud av upplevelser och tjänster. Du samlar poäng varje månad som kan användas till:",
      whyTitle: "Varför Vardagslyx?",
      whyList: [
        "Ett medlemskap istället för flera olika abonnemang",
        "Samla poäng varje månad som aldrig försvinner",
        "Flexibilitet att välja det som passar dig",
        "Nya upplevelser och erbjudanden varje vecka",
        "Enkelt att boka direkt i appen eller på webben"
      ],
      forAllTitle: "För alla",
      forAllText: "Vardagslyx ska vara för alla. Oavsett plånbok ska det vara möjligt att skapa mer livskvalitet i livet. Därför erbjuder vi olika medlemskapsnivåer som passar olika behov och budgetar.",
      ctaTitle: "Redo att börja?",
      ctaDesc: "Bli medlem idag och upptäck en enklare vardag med mer lyx.",
      ctaBtn: "Bli medlem",
      catDescRestaurang: "Kulinariska upplevelser",
      catDescWellness: "Avkoppling och återhämtning",
      catDescTranning: "Stärk kropp och sinne",
      catDescKultur: "Äventyr och nya upplevelser",
      catDescHotell: "Weekendresor och staycations",
      catDescHem: "Det som gör vardagen enklare",
    },
    // Membership page
    membership: {
      heroTitle: "Bli Medlem",
      heroSubtitle: "Upptäck en värld av upplevelser",
      whatTitle: "Vad ingår?",
      whatDesc: "Vardagslyx är ett medlemskap för upplevelser och tjänster i vardagen. Genom ett abonnemang samlar du poäng varje månad som kan användas till restauranger, spa, aktiviteter, hotell och tjänster som gör livet lite enklare.",
      whatDesc2: "Istället för flera olika abonnemang samlar Vardagslyx allt på ett ställe – så att du kan unna dig mer när det passar dig.",
      whatTagline: "Enkelt. Inspirerande. Och lite mer lyx i vardagen.",
      chooseTitle: "Välj ditt medlemskap",
      chooseDesc: "Poäng används som delbetalning på upplevelser – och ger dig tillgång till exklusiva medlemsförmåner.",
      cancelText: "Medlemskapet kan sägas upp när som helst – poängen är kvar tills du återupptar ditt medlemskap",
      popular: "Populärast",
      perMonth: "kr/mån",
      membership: "medlemskap",
      benefitsTitle: "Som medlem får du",
      pointsMonthly: "Poäng varje månad",
      pointsMonthlyDesc: "Använd när du vill",
      exclusiveOffers: "Exklusiva erbjudanden",
      exclusiveOffersDesc: "Bara för medlemmar",
      noHiddenFees: "Inga dolda avgifter",
      noHiddenFeesDesc: "Transparent prissättning",
      cancelAnytime: "Avsluta när som helst",
      cancelAnytimeDesc: "Ingen bindningstid",
      formTitle: "Dina uppgifter",
      name: "Namn",
      email: "E-post",
      phone: "Telefon",
      submit: "Bli medlem",
      processing: "Bearbetar...",
      fillRequired: "Fyll i alla obligatoriska fält",
      alreadyMember: "Redan registrerad som medlem",
      errorGeneric: "Något gick fel",
      termsText: "Genom att bli medlem godkänner du våra villkor.",
      successTitle: "Välkommen till Vardagslyx!",
      successDesc: "Du är nu medlem och kan börja utforska alla våra upplevelser. Dina första poäng har redan lagts till på ditt konto.",
      successBtn: "Utforska upplevelser",
      plans: {
        basic: {
          name: "Bas",
          features: ["Tillgång till alla upplevelser", "Poäng samlas varje månad", "Grundläggande erbjudanden"]
        },
        plus: {
          name: "Plus",
          features: ["Allt i Bas", "Poäng samlas varje månad", "Exklusiva medlemserbjudanden", "Tidig tillgång till nya upplevelser"]
        },
        premium: {
          name: "Premium",
          features: ["Allt i Plus", "Poäng samlas varje månad", "VIP-erbjudanden", "Personlig rådgivning", "Gratis avbokning"]
        }
      }
    },
    // Partner page
    partner: {
      heroTitle: "Bli Partner",
      heroSubtitle: "Nå nya kunder och väx med Vardagslyx",
      benefitsTitle: "Fördelar med att bli partner",
      benefit1: "Nya kunder",
      benefit1Desc: "Nå medlemmar som söker upplevelser",
      benefit2: "Ökad synlighet",
      benefit2Desc: "Exponering på plattformen",
      benefit3: "Fler bokningar",
      benefit3Desc: "Återkommande kunder",
      benefit4: "Inspiration",
      benefit4Desc: "Visa upp er verksamhet",
      faqTitle: "Vanliga frågor från samarbetspartners",
      contactTitle: "Kontakta oss",
      company: "Företagsnamn",
      contactPerson: "Kontaktperson",
      email: "E-post",
      phone: "Telefon",
      message: "Meddelande",
      messagePlaceholder: "Berätta om er verksamhet...",
      submit: "Skicka",
      successMsg: "Tack! Vi kontaktar dig inom kort.",
      faqs: [
        { q: "Varför ska vårt företag samarbeta med Vardagslyx?", a: "Vardagslyx hjälper företag att nå nya kunder som aktivt söker upplevelser och tjänster. Plattformen fungerar som en kanal för att skapa nya bokningar och ökad synlighet." },
        { q: "Behöver vi ge rabatt?", a: "Poängen fungerar som en medlemsförmån för kunden och används som delbetalning eller full betalning med poäng. Ni bestämmer själva vilka erbjudanden eller upplevelser ni vill ge." },
        { q: "Vad kostar det att bli partner?", a: "I många fall är det kostnadsfritt att bli partner. Samarbetet bygger istället på att skapa nya bokningar och kunder genom plattformen." },
        { q: "Vad får vi ut av samarbetet?", a: "list:Som partner får ni:|nya kunder|ökad synlighet|möjlighet till återkommande bokningar|exponering i plattformen och inspiration till medlemmar" },
        { q: "Hur blir vi partner?", a: "Kontakta oss via vår partnersida så berättar vi mer om hur samarbetet fungerar och hur vi kan skapa ett upplägg som passar er verksamhet." }
      ]
    },
    // Customer service page
    customerService: {
      heroTitle: "Kundservice",
      heroSubtitle: "Vanliga frågor och svar om Vardagslyx",
      faqs: [
        { q: "Vad är Vardagslyx?", a: "Vardagslyx är ett medlemskap som samlar upplevelser och tjänster på ett och samma ställe. Som medlem får du poäng varje månad som kan användas hos våra samarbetspartners för upplevelser, aktiviteter och tjänster som förgyller vardagen." },
        { q: "Hur fungerar medlemskapet?", a: "Som medlem betalar du en månadsavgift och får poäng varje månad. Poängen kan användas som delbetalning på upplevelser och tjänster hos våra samarbetspartners. Du väljer själv hur och när du vill använda ditt medlemskap." },
        { q: "Måste jag använda mina poäng varje månad?", a: "Nej. Poängen kan sparas och användas när det passar dig. Du kan även samla poäng över tid för större upplevelser." },
        { q: "Kan jag ge bort mina poäng?", a: "Ja. Poängen kan användas tillsammans med andra eller ges bort till någon du vill dela en upplevelse med." },
        { q: "Hur ofta får jag nya poäng?", a: "Du får poäng varje månad genom ditt medlemskap. Du kan också tjäna nya poäng genom att använda upplevelser och tjänster via plattformen." },
        { q: "Vad kan jag använda poängen till?", a: "list:Poängen kan användas hos våra samarbetspartners inom till exempel:|Restaurang & Mat|Wellness & Spa|Träning & Hälsa|Kultur & Aktiviteter|Hotell & Resor|Hem & Vardagstjänster" },
        { q: "Hur hittar jag upplevelser att använda mina poäng på?", a: "Som medlem får du inspiration och tips om upplevelser och tjänster på mina sidor. Plattformen hjälper dig att upptäcka nya saker att göra, prova på." },
        { q: "Kan jag använda medlemskapet tillsammans med andra?", a: "Ja. Många upplevelser kan delas med vänner, familj eller partner." }
      ]
    },
    // Contact page
    contact: {
      heroTitle: "Kontakta oss",
      heroSubtitle: "Har du frågor eller förslag? Vi hör gärna från dig!",
      formTitle: "Skicka ett meddelande",
      name: "Namn",
      email: "E-post",
      subject: "Ämne",
      message: "Meddelande",
      submit: "Skicka meddelande",
      sending: "Skickar...",
      namePlaceholder: "Ditt namn",
      emailPlaceholder: "din@email.se",
      subjectPlaceholder: "Vad gäller det?",
      messagePlaceholder: "Skriv ditt meddelande här...",
      fillAll: "Fyll i alla fält",
      successToast: "Meddelande skickat!",
      errorToast: "Kunde inte skicka meddelandet",
      successMsg: "Tack för ditt meddelande!",
      successDesc: "Vi har mottagit ditt meddelande och återkommer så snart som möjligt.",
      infoTitle: "Kontaktuppgifter",
      emailLabel: "E-post",
      address: "Adress",
      addressValue: "Storgatan 1\n111 22 Stockholm",
      hoursLabel: "Öppettider",
      hoursWeekday: "Måndag - Fredag: 09:00 - 18:00",
      hoursSaturday: "Lördag: 10:00 - 16:00",
      hoursSunday: "Söndag: Stängt",
    },
    // Footer
    footer: {
      desc: "Samlar upplevelser och tjänster för en enklare och mer lyxig vardag.",
      quickLinks: "Snabblänkar",
      about: "Om Vardagslyx",
      experiences: "Upplevelser",
      membership: "Bli medlem",
      contact: "Kontakt",
      contactTitle: "Kontakt",
      address: "Stockholm, Sverige",
      rights: "Alla rättigheter förbehållna.",
    },
    // Profile page
    profile: {
      title: "Min profil",
      welcome: "Välkommen",
      quote1: "Vad är du intresserad av att hitta på?",
      quote2: "Vad vill du unna dig?",
      quote3: "Här hittar du det bästa som berikar ditt liv",
      memberSince: "Medlem sedan",
      pointsTitle: "Samlade poäng",
      points: "poäng",
      discountTitle: "Rabattkoder",
      noDiscounts: "Du har inga rabattkoder ännu.",
      code: "Kod",
      discount: "Rabatt",
      valid: "Giltig t.o.m.",
      used: "Använd",
      active: "Aktiv",
      changeEmail: "Byt e-post",
      newEmail: "Ny e-post",
      passwordToConfirm: "Lösenord för att bekräfta",
      updateEmail: "Uppdatera e-post",
      changePassword: "Byt lösenord",
      currentPassword: "Nuvarande lösenord",
      newPassword: "Nytt lösenord",
      confirmPassword: "Bekräfta nytt lösenord",
      updatePassword: "Uppdatera lösenord",
      passwordMismatch: "Lösenorden matchar inte",
      passwordTooShort: "Lösenordet måste vara minst 6 tecken",
      subscriptionTitle: "Mitt medlemskap",
      plan: "Plan",
      status: "Status",
      statusActive: "Aktivt",
      statusCancelled: "Avslutat",
      statusInactive: "Inaktivt",
      cancelSubscription: "Avsluta medlemskap",
      cancelConfirm: "Är du säker på att du vill avsluta ditt medlemskap?",
      deleteTitle: "Ta bort profil",
      deleteDesc: "Detta raderar ditt konto, medlemskap, poäng och all data permanent.",
      deleteBtn: "Ta bort min profil",
      deleteConfirm: "Är du helt säker? Detta går inte att ångra.",
      successEmail: "E-post uppdaterad!",
      successPassword: "Lösenord uppdaterat!",
      successCancel: "Medlemskap avslutat",
      successDelete: "Profil borttagen",
      errorGeneric: "Något gick fel",
      notLoggedIn: "Du måste vara inloggad",
      notLoggedInDesc: "Logga in för att se din profil.",
      loginBtn: "Logga in",
      saving: "Sparar...",
      ordersTitle: "Mina Upplevelser",
      noOrders: "Du har inga beställningar ännu.",
      statsTotalOrders: "Beställningar",
      statsTotalExperiences: "Upplevelser",
      statsTotalSpent: "Totalt spenderat (kr)",
      statsByCategory: "Per kategori",
      statsOther: "Övrigt",
      orderDate: "Beställd",
      orderStatus: "Status",
      orderConfirmed: "Bekräftad",
      orderPending: "Väntar",
      orderItems: "Upplevelser",
    },
  },
  en: {
    // Navbar
    nav: {
      about: "What is Vardagslyx?",
      experiences: "Experiences",
      inspiration: "Inspiration",
      faq: "FAQ",
      contact: "Contact",
      customerService: "Customer Service",
      membership: "Become a Member",
      partner: "Become a Partner",
      login: "Log in",
      logout: "Log out",
      cart: "Cart",
      lang: "SV",
    },
    // Home page
    home: {
      heroTitle: "One subscription –",
      heroSubtitle: "for a simpler and more luxurious everyday life",
      heroDesc: "Collect points every month and use them for what you enjoy.",
      heroTagline: "Vardagslyx makes it easy to prioritize what gives energy in everyday life.",
      exploreBtn: "Explore experiences",
      memberBtn: "Become a Member",
      categoriesTitle: "Explore categories",
      categoriesSubtitle: "Discover experiences across different areas",
      valueTitle: "Everything you value, gathered in one membership",
      valueDesc: "Discover experiences, services, and opportunities that brighten your everyday life.",
      valueTagline: "With Vardagslyx, everything is in one place – so you can stop searching and start living.",
      howTitle: "Make it easy to treat yourself",
      howSubtitle: "Collect points every month and use them for what you enjoy.",
      step1Title: "Become a member",
      step1Desc: "Create an account and choose your membership",
      step2Title: "Explore & book",
      step2Desc: "Find experiences that suit you",
      step3Title: "Collect points",
      step3Desc: "Earn points with every booking",
      forAllTitle: "Vardagslyx is for everyone",
      forAllDesc: "Luxury isn't about price – it's about the feeling of prioritizing yourself and your everyday life.",
      forAllTagline: "Regardless of budget, Vardagslyx makes it possible to create more quality of life.",
      forAllQuote: "\"How much more luxury do you want to give yourself?\"",
      simpleTitle: "Simple. Smooth. Accessible.",
      simpleDesc: "With Vardagslyx you get access to a wide range of experiences and services – from spa and massage to dining experiences and activities. All in one place.",
      simplePoint1: "Points that can be used freely",
      simplePoint2: "New experiences every month",
      simplePoint3: "Flexible membership",
      maxTitle: "Get more value in one single membership",
      maxDesc: "Combine services, experiences, and offers to maximize the value of your membership.",
      maxTagline: "The more you use Vardagslyx, the more you get back.",
      maxExploreBtn: "Start exploring",
      maxMemberBtn: "Become a Member",
      memberPromoTitle: "Become a member today",
      memberPromoDesc: "Start collecting points and discover a world of experiences. Your first step towards more everyday luxury.",
      memberPromoBtn: "Become a member now",
      experiences: "experiences",
      popularTitle: "Popular experiences",
      popularSubtitle: "Discover our most popular experiences and treatments",
      showAll: "Show all",
      trustRating: "4.8 rating",
      trustRatingDesc: "Based on thousands of reviews",
      trustCustomers: "50,000+",
      trustCustomersDesc: "Satisfied customers",
      trustExperiences: "200+",
      trustExperiencesDesc: "Unique experiences",
    },
    // Quotes
    quotes: [
      "A simpler everyday. A richer feeling.",
      "More experiences in life. Less planning.",
      "Luxury in everyday life – your way.",
      "A membership for more quality of life.",
    ],
    // Categories
    categories: {
      restaurang: "Restaurant & Food",
      wellness: "Wellness & Spa",
      traning: "Fitness & Health",
      kultur: "Culture & Activities",
      hotell: "Hotels & Travel",
      hem: "Home & Everyday Services",
    },
    // About page
    about: {
      heroTitle: "What is Vardagslyx?",
      heroSubtitle: "A platform that gathers experiences and services for a simpler and more luxurious everyday life",
      intro: "Vardagslyx is a membership that gives you access to experiences and services that make life a little better. We believe luxury isn't about price – it's about the feeling of prioritizing yourself and your everyday life.",
      visionTitle: "Our vision",
      visionText: "In a world full of stress and demands, we want to make it easy to treat yourself more. Vardagslyx gathers everything you need to create more balance, joy, and experiences in everyday life – all in one place.",
      visionMonth1: "One month you might want to:",
      visionList1: ["book a spa", "work out", "order food delivery"],
      visionMonth2: "Another month you might want to:",
      visionList2: ["book a hotel", "try an activity", "discover something new in your city"],
      visionConclusion: "With Vardagslyx, you use your membership for whatever suits you right now.",
      howTitle: "How it works",
      howDesc: "As a member, you get access to a wide range of experiences and services. You collect points every month that can be used for:",
      whyTitle: "Why Vardagslyx?",
      whyList: [
        "One membership instead of several subscriptions",
        "Collect points every month that never expire",
        "Flexibility to choose what suits you",
        "New experiences and offers every week",
        "Easy to book directly in the app or on the web"
      ],
      forAllTitle: "For everyone",
      forAllText: "Vardagslyx is for everyone. Regardless of budget, it should be possible to create more quality of life. That's why we offer different membership levels to suit different needs and budgets.",
      ctaTitle: "Ready to start?",
      ctaDesc: "Become a member today and discover a simpler everyday life with more luxury.",
      ctaBtn: "Become a member",
      catDescRestaurang: "Culinary experiences",
      catDescWellness: "Relaxation and recovery",
      catDescTranning: "Strengthen body and mind",
      catDescKultur: "Adventures and new experiences",
      catDescHotell: "Weekend trips and staycations",
      catDescHem: "What makes everyday life easier",
    },
    // Membership page
    membership: {
      heroTitle: "Become a Member",
      heroSubtitle: "Discover a world of experiences",
      whatTitle: "What's included?",
      whatDesc: "Vardagslyx is a membership for experiences and services in everyday life. Through a subscription, you collect points every month that can be used for restaurants, spa, activities, hotels, and services that make life a little easier.",
      whatDesc2: "Instead of several different subscriptions, Vardagslyx gathers everything in one place – so you can treat yourself more when it suits you.",
      whatTagline: "Simple. Inspiring. And a little more luxury in everyday life.",
      chooseTitle: "Choose your membership",
      chooseDesc: "Points are used as partial payment for experiences – and give you access to exclusive member benefits.",
      cancelText: "The membership can be cancelled at any time – your points remain until you resume your membership",
      popular: "Most popular",
      perMonth: "SEK/mo",
      membership: "membership",
      benefitsTitle: "As a member you get",
      pointsMonthly: "Points every month",
      pointsMonthlyDesc: "Use whenever you want",
      exclusiveOffers: "Exclusive offers",
      exclusiveOffersDesc: "Members only",
      noHiddenFees: "No hidden fees",
      noHiddenFeesDesc: "Transparent pricing",
      cancelAnytime: "Cancel anytime",
      cancelAnytimeDesc: "No commitment",
      formTitle: "Your details",
      name: "Name",
      email: "Email",
      phone: "Phone",
      submit: "Become a member",
      processing: "Processing...",
      fillRequired: "Please fill in all required fields",
      alreadyMember: "Already registered as a member",
      errorGeneric: "Something went wrong",
      termsText: "By becoming a member you agree to our terms.",
      successTitle: "Welcome to Vardagslyx!",
      successDesc: "You are now a member and can start exploring all our experiences. Your first points have already been added to your account.",
      successBtn: "Explore experiences",
      plans: {
        basic: {
          name: "Basic",
          features: ["Access to all experiences", "Points collected every month", "Basic offers"]
        },
        plus: {
          name: "Plus",
          features: ["Everything in Basic", "Points collected every month", "Exclusive member offers", "Early access to new experiences"]
        },
        premium: {
          name: "Premium",
          features: ["Everything in Plus", "Points collected every month", "VIP offers", "Personal guidance", "Free cancellation"]
        }
      }
    },
    // Partner page
    partner: {
      heroTitle: "Become a Partner",
      heroSubtitle: "Reach new customers and grow with Vardagslyx",
      benefitsTitle: "Benefits of becoming a partner",
      benefit1: "New customers",
      benefit1Desc: "Reach members looking for experiences",
      benefit2: "Increased visibility",
      benefit2Desc: "Exposure on the platform",
      benefit3: "More bookings",
      benefit3Desc: "Recurring customers",
      benefit4: "Inspiration",
      benefit4Desc: "Showcase your business",
      faqTitle: "Frequently asked questions from partners",
      contactTitle: "Contact us",
      company: "Company name",
      contactPerson: "Contact person",
      email: "Email",
      phone: "Phone",
      message: "Message",
      messagePlaceholder: "Tell us about your business...",
      submit: "Send",
      successMsg: "Thank you! We will contact you shortly.",
      faqs: [
        { q: "Why should our company partner with Vardagslyx?", a: "Vardagslyx helps businesses reach new customers who are actively looking for experiences and services. The platform serves as a channel to create new bookings and increased visibility." },
        { q: "Do we need to offer discounts?", a: "Points function as a member benefit and are used as partial or full payment. You decide which offers or experiences you want to provide." },
        { q: "What does it cost to become a partner?", a: "In many cases, becoming a partner is free. The collaboration is based on creating new bookings and customers through the platform." },
        { q: "What do we get from the collaboration?", a: "list:As a partner you get:|new customers|increased visibility|opportunity for recurring bookings|exposure on the platform and inspiration to members" },
        { q: "How do we become a partner?", a: "Contact us via our partner page and we'll tell you more about how the collaboration works and how we can create an arrangement that suits your business." }
      ]
    },
    // Customer service page
    customerService: {
      heroTitle: "Customer Service",
      heroSubtitle: "Frequently asked questions about Vardagslyx",
      faqs: [
        { q: "What is Vardagslyx?", a: "Vardagslyx is a membership that gathers experiences and services in one place. As a member, you receive points every month that can be used with our partners for experiences, activities, and services that brighten everyday life." },
        { q: "How does the membership work?", a: "As a member, you pay a monthly fee and receive points every month. Points can be used as partial payment for experiences and services with our partners. You choose how and when to use your membership." },
        { q: "Do I have to use my points every month?", a: "No. Points can be saved and used whenever it suits you. You can also accumulate points over time for bigger experiences." },
        { q: "Can I give away my points?", a: "Yes. Points can be used together with others or given to someone you want to share an experience with." },
        { q: "How often do I get new points?", a: "You receive points every month through your membership. You can also earn new points by using experiences and services through the platform." },
        { q: "What can I use the points for?", a: "list:Points can be used with our partners in areas such as:|Restaurant & Food|Wellness & Spa|Fitness & Health|Culture & Activities|Hotels & Travel|Home & Everyday Services" },
        { q: "How do I find experiences to use my points on?", a: "As a member, you get inspiration and tips about experiences and services on your pages. The platform helps you discover new things to do and try." },
        { q: "Can I use the membership together with others?", a: "Yes. Many experiences can be shared with friends, family, or a partner." }
      ]
    },
    // Contact page
    contact: {
      heroTitle: "Contact us",
      heroSubtitle: "Have questions or suggestions? We'd love to hear from you!",
      formTitle: "Send a message",
      name: "Name",
      email: "Email",
      subject: "Subject",
      message: "Message",
      submit: "Send message",
      sending: "Sending...",
      namePlaceholder: "Your name",
      emailPlaceholder: "your@email.com",
      subjectPlaceholder: "What is it about?",
      messagePlaceholder: "Write your message here...",
      fillAll: "Please fill in all fields",
      successToast: "Message sent!",
      errorToast: "Could not send the message",
      successMsg: "Thank you for your message!",
      successDesc: "We have received your message and will get back to you as soon as possible.",
      infoTitle: "Contact information",
      emailLabel: "Email",
      address: "Address",
      addressValue: "Storgatan 1\n111 22 Stockholm",
      hoursLabel: "Opening hours",
      hoursWeekday: "Monday - Friday: 09:00 - 18:00",
      hoursSaturday: "Saturday: 10:00 - 16:00",
      hoursSunday: "Sunday: Closed",
    },
    // Footer
    footer: {
      desc: "Gathering experiences and services for a simpler and more luxurious everyday life.",
      quickLinks: "Quick links",
      about: "About Vardagslyx",
      experiences: "Experiences",
      membership: "Become a member",
      contact: "Contact",
      contactTitle: "Contact",
      address: "Stockholm, Sweden",
      rights: "All rights reserved.",
    },
    // Profile page
    profile: {
      title: "My profile",
      welcome: "Welcome",
      quote1: "What are you interested in doing?",
      quote2: "What do you want to treat yourself to?",
      quote3: "Here you'll find the best that enriches your life",
      memberSince: "Member since",
      pointsTitle: "Collected points",
      points: "points",
      discountTitle: "Discount codes",
      noDiscounts: "You have no discount codes yet.",
      code: "Code",
      discount: "Discount",
      valid: "Valid until",
      used: "Used",
      active: "Active",
      changeEmail: "Change email",
      newEmail: "New email",
      passwordToConfirm: "Password to confirm",
      updateEmail: "Update email",
      changePassword: "Change password",
      currentPassword: "Current password",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
      updatePassword: "Update password",
      passwordMismatch: "Passwords do not match",
      passwordTooShort: "Password must be at least 6 characters",
      subscriptionTitle: "My membership",
      plan: "Plan",
      status: "Status",
      statusActive: "Active",
      statusCancelled: "Cancelled",
      statusInactive: "Inactive",
      cancelSubscription: "Cancel membership",
      cancelConfirm: "Are you sure you want to cancel your membership?",
      deleteTitle: "Delete profile",
      deleteDesc: "This will permanently delete your account, membership, points and all data.",
      deleteBtn: "Delete my profile",
      deleteConfirm: "Are you completely sure? This cannot be undone.",
      successEmail: "Email updated!",
      successPassword: "Password updated!",
      successCancel: "Membership cancelled",
      successDelete: "Profile deleted",
      errorGeneric: "Something went wrong",
      notLoggedIn: "You must be logged in",
      notLoggedInDesc: "Log in to view your profile.",
      loginBtn: "Log in",
      saving: "Saving...",
      ordersTitle: "My Experiences",
      noOrders: "You have no orders yet.",
      statsTotalOrders: "Orders",
      statsTotalExperiences: "Experiences",
      statsTotalSpent: "Total spent (SEK)",
      statsByCategory: "By category",
      statsOther: "Other",
      orderDate: "Ordered",
      orderStatus: "Status",
      orderConfirmed: "Confirmed",
      orderPending: "Pending",
      orderItems: "Experiences",
    },
  }
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('sv');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        return key;
      }
    }
    return value;
  };

  const toggleLang = () => {
    setLang(prev => prev === 'sv' ? 'en' : 'sv');
  };

  return (
    <LanguageContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}
